import { Div, router, IBaseComponent } from '@codesuma/baseline'
import { HomePage } from './pages/home'
import { DayPage } from './pages/day'
import { YearPage } from './pages/year'
import { CenturyPage } from './pages/century'
import { OccasionPage } from './pages/occasion'
import { TaskPage } from './pages/task'
import { NotePage } from './pages/note'
import { EventPage } from './pages/event'
import { MenuPage } from './pages/menu'
import { SearchPage } from './pages/search'
import { ConverterPage } from './pages/converter'
import { RatesPage } from './pages/rates'
import { CountriesPage } from './pages/countries'
import { CountryPage } from './pages/country'
import { AboutPage } from './pages/about'
import { PrivacyPage } from './pages/privacy'
import { ContactPage } from './pages/contact'
import { WidgetPage } from './pages/widget'
import { StorageNotice } from './components/storage-notice'
import { UpdateNotice } from './components/update-notice'
import appUpdate from './services/app-update'
import { remember, paneled } from './helpers/panes'
import style from './app.module.css'

export const App = () => {
    // Base container
    const base = Div()
    base.addClass(style.base)

    // View
    const view = Div()
    view.addClass(style.view)
    base.append(view)

    // The day page, built ahead of the first tap on a day (see warmDay below).
    // The router asks for it on its first visit and is handed this same one.
    let day: ReturnType<typeof DayPage> | undefined
    const dayPage = () => day ??= DayPage()

    const routes = {
        '/': HomePage,
        // One route for every day (the date travels in the query, ?d=gy-gm-gd)
        // so the router caches a single day page, not one per visited date.
        '/day': dayPage,
        // And one for every year (?y=, in the active calendar system), and one
        // for the block of twenty-five years holding a year.
        '/year': YearPage,
        '/century': CenturyPage,
        // Same query convention: one cached page for every day it adds to.
        '/occasion': OccasionPage,
        // And the task wizard, on the same query convention.
        '/task': TaskPage,
        // And the note page, which takes ?id= for one already written.
        '/note': NotePage,
        // The event editor. Same query convention again, plus ?id= for one of
        // Emrouz's own events and ?p=&s= for one of the phone's.
        '/event': EventPage,
        '/menu': MenuPage,
        '/search': SearchPage,
        '/converter': ConverterPage,
        '/rates': RatesPage,
        // The country holidays directory, and one country's year of them. This
        // one names the country in the path rather than the query, because it
        // is a page about a country and the address should say so — and because
        // the static per-country pages will be generated on these very URLs.
        // The router caches a page per path, so each country gets its own.
        '/countries': CountriesPage,
        '/occasions/country/:slug': CountryPage,
        '/about': AboutPage,
        '/privacy': PrivacyPage,
        '/contact': ContactPage,
        '/widget': WidgetPage,
    }
    // On a wide screen the calendar keeps a phone's width on the right and the
    // day opens in a panel beside it, so the two have to be told apart. Every
    // other page still covers the window the way it does on a phone. Each page
    // is also handed to the panes helper, which needs the instance to reopen a
    // route whose query alone has changed. See app.module.css.
    const PANES: Record<string, string> = { '/': style.calendarPane, '/day': style.detailPane }
    const paned = (path: string, page: () => IBaseComponent<any>) => () => {
        const built = page()
        if (PANES[path]) built.addClass(PANES[path])
        remember(path, built)
        // Side by side, a page does not travel to get here, so it should not
        // wait either. Page() holds its enter back by 200ms to let the page it
        // is replacing leave first, which is a phone's problem — the stylesheet
        // can drop the movement but not the pause, so the page is shown at once
        // here instead. Last of the enter handlers, so the page it reveals is
        // the one the page's own handler has just filled in.
        const instant = (built as { showInstant?: () => void }).showInstant
        if (instant) built.on('enter', () => paneled() && instant.call(built))
        return built
    }

    router.routes(
        Object.fromEntries(Object.entries(routes).map(([path, page]) => [path, paned(path, page)])),
        view,
    )

    // Outside the view, so it survives the page changing under it: storage
    // failing is about the app, not about the page that happened to be open.
    // The update notice shares that spot, so it goes first: were both up, the
    // storage notice would be the one on top.
    const updateNotice = UpdateNotice()
    updateNotice.on('update', appUpdate.open)
    updateNotice.on('later', appUpdate.later)
    base.append(updateNotice, StorageNotice())

    // The calendar is hidden until here (see .base). It waits for the three
    // faces index.html preloads, so the frame that fades up is already in
    // Vazirmatn — otherwise the grid draws in the fallback face and re-flows in
    // full view, halfway through the fade. Each face has to be asked for by
    // name: document.fonts.ready answers on an idle set here, because nothing
    // has laid out yet and so no face has been fetched. The timeout is the
    // safety net — a font that never arrives must not keep the calendar off the
    // screen for longer than a beat.
    // The first time the day's sheet comes up it stalls for half a second
    // while the GPU learns to draw it (see warm in pages/day), so it is built
    // and drawn once out of sight soon after launch. The router appends what
    // it is handed on the page's first visit, so the page goes into the view
    // by hand here, below Baseline: appended through Baseline too, the view
    // would list it twice. On a wide screen the day is a panel, not a sheet,
    // and there is nothing to warm.
    const warmDay = () => {
        if (day || paneled()) return
        const page = dayPage()
        view.el.appendChild(page.el)
        page.warm()
    }

    const faces = ['400 1rem Vazirmatn', '700 1rem Vazirmatn', '400 1rem Quicksand']
    Promise.race([
        Promise.all(faces.map(face => document.fonts.load(face))),
        new Promise(done => setTimeout(done, 600)),
    ]).then(() => {
        base.addClass(style.opened)
        // Once the fade (.opened) is over, at the first quiet moment after it.
        setTimeout(() => {
            if (window.requestIdleCallback) window.requestIdleCallback(warmDay, { timeout: 1000 })
            else warmDay()
        }, 400)
    })

    return base
}
